import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { KeyRound, Loader2, Plug, Trash2, CheckCircle2, XCircle, Save, Lock, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANUAL = '__manual__';
const KEY_URLS = {
  anthropic: { url: 'https://console.anthropic.com/settings/keys', hint: 'console.anthropic.com → API Keys', prefix: 'sk-ant-…' },
  openai: { url: 'https://platform.openai.com/api-keys', hint: 'platform.openai.com → API Keys', prefix: 'sk-…' },
  gemini: { url: 'https://aistudio.google.com/apikey', hint: 'aistudio.google.com → API Keys', prefix: 'AIza…' },
  custom: { url: '', hint: 'OpenAI-uyumlu sunucu (Ollama, vLLM, OpenRouter…)', prefix: 'anahtar' },
};
const ACCENT = { anthropic: 'border-orange-700/40', openai: 'border-emerald-700/40', gemini: 'border-blue-700/40', custom: 'border-gray-700' };

/** Tek sağlayıcı kartı: model seçimi (+ elle giriş), anahtar (yazma-tek), test, sil */
function ProviderCard({ meta, settings, onSaved }) {
  const p = meta.id;
  const hasKey = !!settings.has_key?.[p];
  const listed = meta.models.map(m => m.id);
  const savedModel = settings.models?.[p] || meta.default_model || '';
  const [model, setModel] = useState(savedModel);
  const [manual, setManual] = useState(!!savedModel && !listed.includes(savedModel));
  const [apiKey, setApiKey] = useState('');
  const [endpointUrl, setEndpointUrl] = useState(settings.custom_endpoint_url || '');
  const [busy, setBusy] = useState('');
  const [testResult, setTestResult] = useState(null);

  const save = async () => {
    if (!hasKey && !apiKey.trim()) { toast.error('API anahtarı girin'); return; }
    if (!model.trim()) { toast.error('Model adı girin'); return; }
    setBusy('save');
    try {
      const payload = { models: { [p]: model.trim() } };
      if (apiKey.trim()) payload.keys = { [p]: apiKey.trim() };
      if (p === 'custom') payload.custom_endpoint_url = endpointUrl.trim();
      const res = await axios.put(`${API}/ai/user-settings`, payload, { withCredentials: true });
      setApiKey('');
      toast.success(`${meta.label} ayarları kaydedildi`);
      onSaved(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || (e.response ? `Kaydedilemedi (HTTP ${e.response.status})` : `Kaydedilemedi: ${e.message}`)); }
    finally { setBusy(''); }
  };

  const removeKey = async () => {
    if (!window.confirm(`${meta.label} anahtarınız silinsin mi?`)) return;
    setBusy('del');
    try {
      const res = await axios.put(`${API}/ai/user-settings`, { keys: { [p]: '' } }, { withCredentials: true });
      setTestResult(null);
      toast.success('Anahtar silindi');
      onSaved(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Silinemedi'); }
    finally { setBusy(''); }
  };

  const test = async () => {
    setBusy('test'); setTestResult(null);
    try {
      const res = await axios.post(`${API}/ai/settings/test-key`, { provider: p, model: model.trim(), api_key: apiKey.trim() || undefined, custom_endpoint_url: p === 'custom' ? endpointUrl.trim() || undefined : undefined }, { withCredentials: true });
      setTestResult(res.data);
      if (res.data.success) toast.success(`Bağlantı başarılı · ${res.data.latency_ms} ms`);
      else toast.error(res.data.error || 'Bağlantı başarısız');
    } catch (e) { setTestResult({ success: false, error: e.response?.data?.detail || 'Test isteği başarısız' }); }
    finally { setBusy(''); }
  };

  const isDefault = settings.default_provider === p;
  return (
    <div className={`rounded-lg border ${ACCENT[p]} bg-gray-900/60 p-4 space-y-3`} data-testid={`ai-provider-card-${p}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-white">{meta.label}</span>
        {hasKey ? <Badge className="bg-emerald-800 text-emerald-100 text-[10px]" data-testid={`ai-key-status-${p}`}>Kayıtlı · {settings.keys_masked?.[p]}</Badge>
          : <Badge className="bg-gray-800 text-gray-400 text-[10px]" data-testid={`ai-key-status-${p}`}>Anahtar yok</Badge>}
        {isDefault && <Badge className="bg-[#E67E22]/20 text-[#E67E22] text-[10px] flex items-center gap-1"><Star className="w-3 h-3" /> Varsayılan</Badge>}
        {KEY_URLS[p].url && <a href={KEY_URLS[p].url} target="_blank" rel="noopener noreferrer" className="ml-auto text-[10px] text-blue-400 underline">{KEY_URLS[p].hint}</a>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-gray-300 text-xs">Model</Label>
          {meta.models.length > 0 && !manual ? (
            <Select value={listed.includes(model) ? model : meta.default_model} onValueChange={v => (v === MANUAL ? setManual(true) : setModel(v))}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9" data-testid={`ai-model-select-${p}`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-72">
                {meta.models.map(m => <SelectItem key={m.id} value={m.id}>{m.label}{m.id === meta.default_model ? ' · önerilen' : ''}</SelectItem>)}
                <SelectItem value={MANUAL}>Elle model adı gir…</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex gap-2">
              <Input value={model} onChange={e => setModel(e.target.value)} placeholder="model kimliği (örn. claude-sonnet-5)" className="bg-gray-800 border-gray-700 text-white h-9 font-mono text-xs" data-testid={`ai-model-input-${p}`} />
              {meta.models.length > 0 && <Button variant="ghost" size="sm" className="h-9 text-xs text-gray-400" onClick={() => { setManual(false); setModel(listed.includes(savedModel) ? savedModel : meta.default_model); }}>Listeden seç</Button>}
            </div>
          )}
        </div>
        <div className="space-y-1">
          <Label className="text-gray-300 text-xs flex items-center gap-1"><KeyRound className="w-3 h-3" /> API Anahtarı</Label>
          <div className="flex gap-2">
            <Input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} autoComplete="off"
              placeholder={hasKey ? 'Değiştirmek için yeni anahtar girin' : `${KEY_URLS[p].prefix} (yazma-tek)`}
              className="bg-gray-800 border-gray-700 text-white h-9 font-mono text-xs" data-testid={`ai-key-input-${p}`} />
            {hasKey && <Button variant="outline" size="sm" onClick={removeKey} disabled={!!busy} className="h-9 border-red-800/50 text-red-300 hover:bg-red-900/30" title="Anahtarı sil" data-testid={`ai-key-delete-${p}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
          </div>
        </div>
      </div>
      {p === 'custom' && (
        <div className="space-y-1">
          <Label className="text-gray-300 text-xs">Endpoint URL (OpenAI-uyumlu, …/v1)</Label>
          <Input value={endpointUrl} onChange={e => setEndpointUrl(e.target.value)} placeholder="https://sunucu/v1" className="bg-gray-800 border-gray-700 text-white h-9 text-xs" data-testid="ai-endpoint-input-custom" />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={save} disabled={!!busy} className="bg-[#E67E22] hover:bg-[#d3701c] text-white h-8 text-xs" data-testid={`ai-save-${p}`}>
          {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Kaydet
        </Button>
        <Button variant="outline" onClick={test} disabled={!!busy || (!hasKey && !apiKey.trim())} className="h-8 border-gray-600 text-gray-200 text-xs" data-testid={`ai-test-${p}`}>
          {busy === 'test' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plug className="w-3.5 h-3.5 mr-1" />} Bağlantıyı Test Et
        </Button>
        {testResult && (
          <span className={`text-xs flex items-center gap-1 ${testResult.success ? 'text-emerald-300' : 'text-red-300'}`} data-testid={`ai-test-result-${p}`}>
            {testResult.success ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
            {testResult.success ? `${testResult.model} · ${testResult.latency_ms} ms` : testResult.error}
          </span>
        )}
      </div>
    </div>
  );
}

/** Profil › Yapay Zekâ: kullanıcının KENDİ anahtarları — tüm modüllerde yalnızca kendi işlemlerinde kullanılır. */
export function AIAyarlarFormu({ onSaved }) {
  const [providers, setProviders] = useState([]);
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([axios.get(`${API}/ai/models`, { withCredentials: true }), axios.get(`${API}/ai/user-settings`, { withCredentials: true })]);
      setProviders(p.data.providers);
      setSettings(s.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Yapay zekâ ayarları yüklenemedi'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const applySaved = (v) => { setSettings(v); onSaved?.(v); };
  const setDefault = async (p) => {
    try { applySaved((await axios.put(`${API}/ai/user-settings`, { default_provider: p }, { withCredentials: true })).data); toast.success('Varsayılan sağlayıcı güncellendi'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
  };

  if (!settings) return <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor…</div>;
  const withKey = providers.filter(p => settings.has_key?.[p.id]);

  return (
    <div className="space-y-4" data-testid="ai-ayarlar-user">
      <div className="text-xs text-cyan-200 bg-cyan-900/20 border border-cyan-800/40 rounded-lg p-3 flex items-start gap-2" data-testid="ai-kisisel-anahtar-notu">
        <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>Bu anahtarlar <b>yalnızca sizin</b> işlemlerinizde kullanılır — başka hiçbir kullanıcı (yönetici dahil) sizin anahtarınızla çalışamaz. ISY Uzman Asistan, Mevzuat Analizi, Ekipman Zekâsı ve gelecekteki tüm yapay zekâ özellikleri burada girdiğiniz anahtar ve model tercihini kullanır.</span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Label className="text-gray-300 text-xs">Varsayılan sağlayıcı</Label>
        <Select value={settings.default_provider || ''} onValueChange={setDefault} disabled={withKey.length === 0}>
          <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 w-64 text-xs" data-testid="ai-default-provider"><SelectValue placeholder={withKey.length ? 'Seçin' : 'Önce bir anahtar kaydedin'} /></SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700 text-white">
            {withKey.map(p => <SelectItem key={p.id} value={p.id}>{p.label} · {settings.models?.[p.id]}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-gray-500">Modül özel bir sağlayıcı seçtirmiyorsa bu kullanılır.</span>
      </div>
      <div className="grid grid-cols-1 gap-3">
        {providers.map(meta => <ProviderCard key={meta.id} meta={meta} settings={settings} onSaved={applySaved} />)}
      </div>
      <p className="text-[10px] text-gray-500">Anahtarlar sunucuda AES-256-GCM ile şifrelenir; yanıtlarda ve loglarda daima maskelidir, tarayıcıya düz metin dönmez.</p>
    </div>
  );
}

export default AIAyarlarFormu;
